// src/pages/auth/RoleSelectionPage.jsx
import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { USER_ROLES } from '../../config/auth0.config';

/**
 * Role Selection Page
 * Shown before Auth0 signup to let users choose their role
 */
export default function RoleSelectionPage() {
  const { loginWithRedirect } = useAuth0();
  const [selectedRole, setSelectedRole] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const roles = [
    {
      code: USER_ROLES.USER,
      title: 'I\'m a Visitor',
      description: 'Browse farms, book experiences, and save your favorites',
      icon: '👤',
      features: [
        'Browse and search farms',
        'Book farm experiences',
        'Save bookmarks and favorites',
        'Write reviews',
      ],
    },
    {
      code: USER_ROLES.FARMER,
      title: 'I\'m a Farmer',
      description: 'List your farm and manage experiences',
      icon: '🌾',
      features: [
        'Create and manage farm listings',
        'Add experiences and events',
        'Manage bookings',
        'View analytics',
      ],
    },
  ];

  const handleRoleSelect = (roleCode) => {
    setSelectedRole(roleCode);
  };

  const handleContinue = async () => {
    if (!selectedRole) {
      alert('Please select a role to continue');
      return;
    }

    setIsLoading(true);

    try {
      // Redirect to Auth0 signup with role information
      await loginWithRedirect({
        authorizationParams: {
          screen_hint: 'signup',
          role_hint: selectedRole,
        },
        appState: {
          returnTo: '/auth/callback',
          roleCode: selectedRole,
        },
      });
    } catch (error) {
      console.error('Failed to redirect to signup:', error);
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}>
      <div style={{
        maxWidth: '1000px',
        width: '100%',
        background: 'white',
        borderRadius: '16px',
        padding: '3rem',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: 'bold',
          textAlign: 'center',
          marginBottom: '1rem',
          color: '#333',
        }}>
          Welcome to FarmCiti! 🌱
        </h1>
        
        <p style={{
          textAlign: 'center',
          fontSize: '1.1rem',
          color: '#666',
          marginBottom: '3rem',
        }}>
          Choose how you'd like to use FarmCiti
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          marginBottom: '3rem',
        }}>
          {roles.map((role) => (
            <div
              key={role.code}
              onClick={() => handleRoleSelect(role.code)}
              style={{
                border: `3px solid ${selectedRole === role.code ? '#4CAF50' : '#ddd'}`,
                borderRadius: '12px',
                padding: '2rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: selectedRole === role.code ? '#f0fff4' : 'white',
              }}
              onMouseEnter={(e) => {
                if (selectedRole !== role.code) {
                  e.currentTarget.style.borderColor = '#bbb';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedRole !== role.code) {
                  e.currentTarget.style.borderColor = '#ddd';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <div style={{
                fontSize: '3rem',
                textAlign: 'center',
                marginBottom: '1rem',
              }}>
                {role.icon}
              </div>
              
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                marginBottom: '0.5rem',
                textAlign: 'center',
                color: '#333',
              }}>
                {role.title}
              </h3>
              
              <p style={{
                textAlign: 'center',
                color: '#666',
                marginBottom: '1.5rem',
              }}>
                {role.description}
              </p>
              
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
              }}>
                {role.features.map((feature, idx) => (
                  <li
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      color: '#555',
                    }}
                  >
                    <span style={{ color: '#4CAF50' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              
              {selectedRole === role.code && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.5rem',
                  background: '#4CAF50',
                  color: 'white',
                  borderRadius: '8px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                }}>
                  Selected ✓
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem',
        }}>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '1rem 2rem',
              fontSize: '1.1rem',
              border: '2px solid #ddd',
              background: 'white',
              color: '#333',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            Go Back
          </button>
          
          <button
            onClick={handleContinue}
            disabled={!selectedRole || isLoading}
            style={{
              padding: '1rem 2rem',
              fontSize: '1.1rem',
              border: 'none',
              background: selectedRole ? '#4CAF50' : '#ccc',
              color: 'white',
              borderRadius: '8px',
              cursor: selectedRole ? 'pointer' : 'not-allowed',
              fontWeight: 'bold',
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? 'Please wait...' : 'Continue to Sign Up →'}
          </button>
        </div>

        <p style={{
          textAlign: 'center',
          marginTop: '2rem',
          fontSize: '0.9rem',
          color: '#666',
        }}>
          Already have an account?{' '}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              loginWithRedirect();
            }}
            style={{
              color: '#4CAF50',
              fontWeight: 'bold',
              textDecoration: 'none',
            }}
          >
            Log In
          </a>
        </p>
      </div>
    </div>
  );
}
